import { PortfolioResource } from '@appTypes/PortfolioResource';
import { EeqCampaignOverridesResponse } from '@appTypes/essential-ui/EeqCampaignOverridesResponse';
import { isDealerOptIn } from '@components/dealsheet/Helpers';
import { RoutePaths } from '@enums/RoutePaths';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { usePortfolioFilter } from '@hooks/UsePortfolioFilter';
import { getOfferedDealType } from '@services/api/GenericClient';
import { AppState } from '@store/AppState';
import {
  setCurrentDealType,
  setIsPortfolioFilterReset,
  setPortfolioShouldRefresh,
  setPortfolioShouldRefreshDealCount,
} from '@store/portfolio/PortfolioActions';
import { PortfolioState } from '@store/portfolio/PortfolioReducer';
import { setRightMobileLayout } from '@store/service-portfolio/ServiceActions';
import type { ServiceState } from '@store/service-portfolio/ServiceReducer';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { HeaderLayout } from '../shared-layout/mobile/header-layout/HeaderLayout';
import RightModalLayout from '../shared-layout/mobile/right-modal/RightModalLayout';
import LeftPanel from '../shared/left-panel/LeftPanel';
import Loader from '../shared/loader/Loader';
import { PortfolioLeftContainer } from './components/PortfolioLeftContainer';
import PortfolioFilter from './filter-section/PortfolioFilter';
import PortfolioFilterSection from './filter-section/PortfolioFilterSection';
import PortfolioTableSection from './table-section/PortfolioTableSection';
import PortfolioBulkActions from './table-section/bulk-actions/PortfolioBulkActions';

export type PortfolioProps = {};

export const Portfolio: FC<PortfolioProps> = () => {
  const currentDealType = useParams().dealType ?? 'all';
  const currentStoreId = localStorage.getItem('am.storeCode') ?? '';
  const navigate = useNavigate();

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [toggleAllRowsValue, setToggleAllRowsValue] = useState<boolean>(false);
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [filteredRecordsCount, setFilteredRecordsCount] = useState<number>(0);
  const [isAllRowsSelected, setIsAllRowsSelected] = useState<boolean>(false);
  const { dealType, isLoading } = useSelector<AppState, PortfolioState>((state) => state.portfolio);
  const [isBulkOperation, setIsBulkOperation] = useState<boolean>(false);
  const [isPortfolioFilterSectionVisible, setIsPortfolioFilterSectionVisible] =
    useState<boolean>(false);
  const dispatch = useDispatch();
  const { isMobileDevice } = useCheckDevice();
  const { resetFilters, hasFilters } = usePortfolioFilter();
  const { selectedPortfolioType, rightMobileLayout } = useSelector<AppState, ServiceState>(
    (state) => state.service,
  );
  const zIndex = isMobileDevice ? (rightMobileLayout ? 201 : 102) : 0;
  const [loadingDealsheet, setLoadingDealsheet] = useState<boolean>(false);
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(true);

  const reset = () => {
    resetFilters(dealType);
    dispatch(setIsPortfolioFilterReset(true));
  };

  const toggleBulkActions = (isBulkActionsVisible: boolean): void => {
    if (isBulkActionsVisible) {
      setIsBulkOperation(true);
      setIsPortfolioFilterSectionVisible(false);
    } else {
      setIsBulkOperation(false);
      setIsPortfolioFilterSectionVisible(true);
    }
  };

  const onPortfolioSelection = (dealType: string): void => {
    navigate(`/portfolio/${dealType}`);
  };

  const openDealSheet = (vin: string, dealerId: string, dealType: string) => {
    if (isDealerOptIn(dealerId)) {
      getOfferedDealType(dealerId, vin).then((response: EeqCampaignOverridesResponse) => {
        var offeredDealType = response?.offeredDealType;
        var urlDealType =
          offeredDealType !== undefined && offeredDealType !== null && offeredDealType !== ''
            ? offeredDealType.toLowerCase()
            : dealType;
        navigate(`/dealsheet/${vin}/${dealerId}/offer/1/${urlDealType}/bps`);
      });
    } else {
      navigate(`/dealsheet/${vin}/${dealerId}/offer/1/${dealType}/bps`);
    }
  };

  const selecteRowIdsCount = Object.keys(selectedRows).length;

  const toggleSelectedRows = (value: boolean) => {
    setRefreshCount(refreshCount + 1);
    setToggleAllRowsValue(value);
  };

  /** Effect used to show/hide the Bulk Actions container based on the selected count of rows */
  useEffect(() => {
    if (selecteRowIdsCount > 0) {
      toggleBulkActions(true);
    } else {
      toggleBulkActions(false);
    }
  }, [selecteRowIdsCount]);

  useEffect(() => {
    const pushOrSmsNotificationUrl = localStorage.getItem('pushOrSmsNotificationUrl');
    if (pushOrSmsNotificationUrl != null && pushOrSmsNotificationUrl !== '') {
      setLoadingDealsheet(true);
      setIsVisible(false);

      const url = new URL(pushOrSmsNotificationUrl).pathname;
      const lastSlashIndex = url.lastIndexOf('/');
      const notificationType = url.substring(lastSlashIndex + 1).replace(/%20/g, ' ');
      const secondLastSlashIndex = url.lastIndexOf('/', lastSlashIndex - 1);
      const dealSheetUrl = url.substring(0, secondLastSlashIndex);
      if (
        notificationType.toLowerCase() === 'customer greeted' ||
        notificationType.toLowerCase() === 'customergreeted'
      ) {
        navigate(RoutePaths.Service);
      } else {
        localStorage.removeItem('pushOrSmsNotificationUrl');
        window.location.href = dealSheetUrl;
      }
    }
  }, []);

  useEffect(() => {
    toggleBulkActions(false);
    toggleSelectedRows(false);
  }, []);

  useEffect(() => {
    if (!isLoading && !loadingDealsheet) {
      setIsVisible(true);
    }
  }, [isLoading, loadingDealsheet]);

  useEffect(() => {
    dispatch(setCurrentDealType(currentDealType));
    setShouldRefresh(false);
  }, [currentDealType]);

  useEffect(() => {
    if (dealType) {
      dispatch(setPortfolioShouldRefresh(true, dealType));
    }
  }, [dealType]);

  useEffect(() => {
    if (shouldRefresh) {
      dispatch(setPortfolioShouldRefreshDealCount(true, currentDealType));
    }
  }, [shouldRefresh]);

  return (
    <>
      {(isLoading || loadingDealsheet) && <Loader zIndex={zIndex} />}
      {isMobileDevice ? (
        <>
          <ServicePortfolioTypeWrapper isMobileDevice={isMobileDevice}>
            <HeaderLayout type='loyalty' />
          </ServicePortfolioTypeWrapper>
          <RightModalLayout
            enable={rightMobileLayout}
            enableReset={hasFilters(selectedPortfolioType)}
            resetButtonAction={reset}
            hideFilter={() => dispatch(setRightMobileLayout(false))}
          >
            <PortfolioFilter />
          </RightModalLayout>
        </>
      ) : (
        <PortfolioFilterSection
          toggleBulkActions={toggleSelectedRows}
          isPortfolioFilterSectionVisible={isPortfolioFilterSectionVisible}
        />
      )}
      {isMobileDevice && isVisible && (
        <PortfolioTableSection
          dealType={dealType}
          storeId={currentStoreId}
          filteredRecordsCount={filteredRecordsCount}
          toggleAllRowsValue={toggleAllRowsValue}
          refreshCount={refreshCount}
          setFilteredRecordsCount={setFilteredRecordsCount}
          setSelectedRows={setSelectedRows}
          setIsAllRowsSelected={setIsAllRowsSelected}
          onPortfolioSelection={onPortfolioSelection}
          openDealSheet={openDealSheet}
          isBulkOperation={isBulkOperation}
        />
      )}
      {!isMobileDevice && isVisible && (
        <Wrapper data-testid='portfolio-container'>
          <PortfolioBulkActions
            isVisible={isBulkOperation}
            selectedRecords={selectedRows as PortfolioResource[]}
            totalRecordsCount={filteredRecordsCount}
            isAllRowsSelected={isAllRowsSelected}
            toggleSelectedRows={toggleSelectedRows}
            dealType={dealType}
          />
          <PortfolioLeftContainer data-testid='portfolio-left-container'>
            <LeftPanel
              backgroundColor={theme.palette.white}
              className='no-padding'
              padding='10px 0 0 0'
            >
              <PortfolioTableSection
                dealType={dealType}
                storeId={currentStoreId}
                filteredRecordsCount={filteredRecordsCount}
                toggleAllRowsValue={toggleAllRowsValue}
                refreshCount={refreshCount}
                setFilteredRecordsCount={setFilteredRecordsCount}
                setSelectedRows={setSelectedRows}
                setIsAllRowsSelected={setIsAllRowsSelected}
                onPortfolioSelection={onPortfolioSelection}
                openDealSheet={openDealSheet}
                isBulkOperation={isBulkOperation}
              />
            </LeftPanel>
          </PortfolioLeftContainer>
          <PortfolioFilterSection
            toggleBulkActions={toggleSelectedRows}
            isPortfolioFilterSectionVisible={isPortfolioFilterSectionVisible}
          />
        </Wrapper>
      )}
    </>
  );
};

type ServicePortfolioTypeWrapperProps = {
  isMobileDevice: boolean;
};

export const Wrapper = styled.div`
  display: flex;
`;

const ServicePortfolioTypeWrapper = styled.span<ServicePortfolioTypeWrapperProps>`
  display: flex;
  line-height: 16px;

  background-color: ${theme.palette.white};

  ${(props) => (!props.isMobileDevice ? `padding: 11px 12px;` : '')}

  & div:first-child {
    display: flex;
    width: 100%;
  }
  &:hover {
    cursor: pointer;
  }
  .selected-type {
    border-bottom: 3px solid ${theme.palette.text.purple};
    padding-bottom: 4px;
    & > * {
      color: ${theme.palette.text.purple};
    }
  }
  .selectedPortfolio {
    margin-left: 20px;
  }
`;
